import { useTranslation } from "react-i18next";
import { ContentKind } from "reedy-data/models";
import { Text } from "components/text";
import type { OnixImportEvent } from "../../hooks/useMetadataEvents";
import { kindLabel } from "./kindLabel";

const MAX_EXAMPLE_KEYWORDS = 3;

export const hasAdoptedContent = (event: OnixImportEvent): boolean =>
  (event.onixImport.adoption ?? []).some((entry) => entry.count > 0);

export const OnixImportTooltipContent = ({
  event,
}: {
  event: OnixImportEvent;
}) => {
  const { t } = useTranslation(["productDetails"]);
  const { onixImport } = event;
  const counts = onixImport.counts ?? {};
  const changed = onixImport.changed ?? [];
  const adopted = hasAdoptedContent(event);

  const keywordAdoption = (onixImport.adoption ?? []).find(
    (entry) => entry.kind === ContentKind.KEYWORDS && entry.count > 0,
  );
  const exampleKeywords = (keywordAdoption?.adopted ?? []).slice(
    0,
    MAX_EXAMPLE_KEYWORDS,
  );

  const countRows: [string, number][] = [
    [
      t("productDetails:salesData.events.onixImport.counts.keywords"),
      counts.keywords ?? 0,
    ],
    [
      t("productDetails:salesData.events.onixImport.counts.themaCodes"),
      counts.themaCodes ?? 0,
    ],
    [
      t("productDetails:salesData.events.onixImport.counts.descriptions"),
      counts.descriptions ?? 0,
    ],
  ];

  return (
    <div className="min-w-52">
      <Text as="p" variant="bodyStrong">
        {t("productDetails:salesData.events.onixImport.subjects")}
      </Text>
      <dl className="mt-1 space-y-0.5">
        {countRows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4">
            <Text as="dt" variant="muted">
              {label}
            </Text>
            <Text as="dd" variant="body">
              {value}
            </Text>
          </div>
        ))}
      </dl>

      <Text as="p" variant="body" className="mt-2">
        <Text as="span" variant="muted">
          {t("productDetails:salesData.events.onixImport.changed")}:
        </Text>{" "}
        {changed.length > 0
          ? changed.map((kind) => kindLabel(t, kind)).join(", ")
          : t("productDetails:salesData.events.onixImport.nothingChanged")}
      </Text>

      {adopted && (
        <div className="mt-2">
          <Text as="p" variant="bodyStrong" className="text-badge-success">
            {t("productDetails:salesData.events.onixImport.adoption.title")}
          </Text>
          {keywordAdoption && (
            <Text as="p" variant="body" className="mt-0.5">
              {t(
                "productDetails:salesData.events.onixImport.adoption.keywords",
                {
                  count: keywordAdoption.count,
                },
              )}
            </Text>
          )}
          {exampleKeywords.length > 0 && (
            <Text as="p" variant="muted" className="mt-0.5">
              {t(
                "productDetails:salesData.events.onixImport.adoption.examples",
                {
                  examples: exampleKeywords.join(", "),
                },
              )}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};
